import { useEffect, useState } from "react";
import { Mail, MailOpen, Clock, CheckCircle2, ChevronDown } from "lucide-react";
import { adminGetMessages, adminSetMessageStatus } from "@/lib/api";
import { toast } from "sonner";

const STATUSES = [
  { id: "unread", label: "Unread", icon: Mail, cls: "bg-[#FF8473]/20 text-[#B3402F]" },
  { id: "in_progress", label: "In Progress", icon: Clock, cls: "bg-[#FFB56B]/20 text-[#9A5A12]" },
  { id: "completed", label: "Completed", icon: CheckCircle2, cls: "bg-[#D7E85C]/30 text-[#5C6B12]" },
];
const badge = (s) => STATUSES.find((x) => x.id === s) || STATUSES[0];

export default function MessagesAdmin() {
  const [messages, setMessages] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(null);

  const load = () => adminGetMessages(filter || undefined).then(setMessages).catch(() => toast.error("Failed to load messages"));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const setStatus = async (id, status) => {
    try {
      await adminSetMessageStatus(id, status);
      toast.success("Status updated");
      load();
    } catch { toast.error("Update failed"); }
  };

  return (
    <div data-testid="admin-messages">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl text-[#1F3864] mb-1">Messages</h1>
          <p className="text-[#1F3864]/60">Enquiries from the site contact form.</p>
        </div>
        <select data-testid="messages-filter" value={filter} onChange={(e) => setFilter(e.target.value)}
          className="border border-[#1F3864]/20 rounded-full px-4 py-2 text-sm font-bold text-[#1F3864] bg-white">
          <option value="">All</option>
          {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
      </div>

      <div className="space-y-3">
        {messages.length === 0 ? (
          <p data-testid="messages-empty" className="text-center text-[#1F3864]/50 text-sm py-16 bg-white rounded-2xl border border-[#1F3864]/10">No messages yet.</p>
        ) : (
          messages.map((m) => {
            const st = badge(m.status);
            return (
              <div key={m.id} data-testid={`message-${m.id}`} className="bg-white rounded-2xl border border-[#1F3864]/10 overflow-hidden">
                <button onClick={() => setOpen(open === m.id ? null : m.id)} className="w-full px-5 py-4 flex flex-wrap items-center gap-4 text-left">
                  <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${st.cls}`}>
                    <st.icon size={13} /> {st.label}
                  </span>
                  <div className="flex-1 min-w-[200px]">
                    <div className="font-bold text-[#1F3864]">{m.subject || "(no subject)"}</div>
                    <div className="text-xs text-[#1F3864]/50">{m.name} · {m.email} · {new Date(m.created_at).toLocaleString()}</div>
                  </div>
                  <ChevronDown size={18} className={`text-[#1F3864]/40 transition-transform ${open === m.id ? "rotate-180" : ""}`} />
                </button>
                {open === m.id && (
                  <div className="px-5 pb-5 border-t border-[#1F3864]/10 pt-4">
                    <p className="text-sm text-[#1F3864]/80 whitespace-pre-wrap mb-4">{m.message}</p>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-[#1F3864]/50">Mark as:</span>
                      {STATUSES.map((s) => (
                        <button key={s.id} data-testid={`msg-status-${m.id}-${s.id}`} onClick={() => setStatus(m.id, s.id)}
                          className={`px-3 py-1.5 rounded-full text-xs font-bold border ${m.status === s.id ? "bg-[#1F3864] text-[#D7E85C] border-[#1F3864]" : "border-[#1F3864]/20 text-[#1F3864] hover:bg-[#F4F1EC]"}`}>
                          {s.label}
                        </button>
                      ))}
                      <a href={`mailto:${m.email}?subject=Re: ${encodeURIComponent(m.subject || "Your enquiry")}`}
                        className="ml-auto inline-flex items-center gap-1.5 text-xs font-bold text-[#00AF95] hover:underline">
                        <MailOpen size={13} /> Reply by email
                      </a>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
